import { LayerColors, DetailLevel, LayerZoomThresholds, LayerZoomThreshold } from '@/types/canvas';
import type { StyleSpecification } from 'mapbox-gl';
import { getColorPresetByScheme } from '@/lib/api';

/**
 * Pomocná funkce pro získání min/max hodnot z thresholdu
 */
function getThresholdValues(threshold: LayerZoomThreshold): { min: number; max?: number } {
  if (typeof threshold === 'number') {
    return { min: threshold };
  }
  return { min: threshold.min ?? 0, max: threshold.max };
}

/**
 * Určuje, zda je vrstva viditelná podle zoomu a detail levelu
 */
export function isLayerVisible(
  layerId: string,
  zoom: number,
  detailLevel: DetailLevel = 'standard',
  showMinorRoads: boolean = true,
  showPaths: boolean = true,
  showPedestrian: boolean = true,
  customZoomThresholds?: LayerZoomThresholds
): boolean {
  // Mapování layerId na klíče v customZoomThresholds
  const layerIdToThresholdKey: Record<string, keyof LayerZoomThresholds> = {
    'road-motorway': 'road_motorway',
    'road-major-link': 'road_major_link',
    'road-major': 'road_major',
    'road-minor-link': 'road_minor_link',
    'road-minor': 'road_minor',
    'road-path': 'road_path',
    'road-residential': 'road_residential',
    'road-pedestrian': 'road_pedestrian',
    'road-footway-cycleway': 'road_footway_cycleway',
    'road-path-track': 'road_path_track',
    'building-outline': 'building_outline',
  };

  // Pokud máme custom thresholdy a detailLevel je 'custom', použijeme je
  if (detailLevel === 'custom' && customZoomThresholds) {
    const thresholdKey = layerIdToThresholdKey[layerId];
    if (thresholdKey && customZoomThresholds[thresholdKey] !== undefined) {
      const threshold = customZoomThresholds[thresholdKey]!;
      const { min, max } = getThresholdValues(threshold);

      // Kontrola minimálního zoomu
      if (zoom < min) return false;

      // Kontrola maximálního zoomu (pokud je nastaven)
      if (max !== undefined && zoom > max) return false;

      // Pokud jsme prošli všechny kontroly, vrstva je viditelná
      // (ale ještě musíme zkontrolovat showMinorRoads/showPaths/showPedestrian)
    } else {
      // Pokud není definován custom threshold, použijeme standardní
      detailLevel = 'standard';
    }
  }

  // Definice minimálních zoomů pro různé vrstvy podle detail levelu
  const layerZoomThresholds: Record<string, Record<DetailLevel, number>> = {
    'road-motorway': { minimal: 5, standard: 4, high: 4, maximum: 4, custom: 4 },
    'road-major-link': { minimal: 8, standard: 6, high: 6, maximum: 6, custom: 6 },
    'road-major': { minimal: 6, standard: 5, high: 5, maximum: 5, custom: 5 },
    'road-minor-link': { minimal: 10, standard: 7, high: 7, maximum: 7, custom: 7 },
    'road-minor': { minimal: 8, standard: 6, high: 6, maximum: 6, custom: 6 },
    'road-path': { minimal: 10, standard: 7, high: 7, maximum: 7, custom: 7 },
    'road-residential': { minimal: 10, standard: 7, high: 7, maximum: 7, custom: 7 },
    'road-pedestrian': { minimal: 12, standard: 8, high: 8, maximum: 8, custom: 8 },
    'road-footway-cycleway': { minimal: 14, standard: 9, high: 9, maximum: 9, custom: 9 },
    'road-path-track': { minimal: 14, standard: 9, high: 9, maximum: 9, custom: 9 },
    'building-outline': { minimal: 8, standard: 6, high: 6, maximum: 6, custom: 6 },
  };

  const threshold = layerZoomThresholds[layerId]?.[detailLevel];
  if (threshold === undefined) return true; // Vrstvy bez definice jsou vždy viditelné

  // Kontrola zoomu
  if (zoom < threshold) return false;

  // Kontrola specifických nastavení
  if (layerId === 'road-residential' || layerId === 'road-path' || layerId === 'road-minor-link') {
    if (!showMinorRoads) return false;
  }

  if (layerId === 'road-footway-cycleway' || layerId === 'road-path-track') {
    if (!showPaths) return false;
  }

  if (layerId === 'road-pedestrian') {
    if (!showPedestrian) return false;
  }

  return true;
}

/**
 * Generuje Mapbox GL style objekt s custom barvami vrstev
 */
export function buildMapboxStyle(
  colors: LayerColors,
  showLabels: boolean = true,
  showBuildings: boolean = true,
  mapLayer: string = 'standard',
  zoom: number = 12,
  detailLevel: DetailLevel = 'standard',
  showMinorRoads: boolean = true,
  showPaths: boolean = true,
  showPedestrian: boolean = true,
  customZoomThresholds?: LayerZoomThresholds
): StyleSpecification {
  return {
    version: 8,
    name: 'Custom Style',
    sources: {
      'mapbox': {
        type: 'vector',
        url: 'mapbox://mapbox.mapbox-streets-v8'
      }
    },
    glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
    layers: [
      // 1. Background
      {
        id: 'background',
        type: 'background',
        paint: {
          'background-color': colors.background
        }
      },

      // 2. Land
      {
        id: 'land',
        type: 'fill',
        source: 'mapbox',
        'source-layer': 'landuse',
        filter: ['in', 'class', 'park', 'cemetery', 'hospital', 'school'],
        paint: {
          'fill-color': colors.land,
          'fill-opacity': 0.3
        }
      },

      // 3. Water
      {
        id: 'water',
        type: 'fill',
        source: 'mapbox',
        'source-layer': 'water',
        paint: {
          'fill-color': colors.water,
          'fill-opacity': 0.9
        }
      },

      // 4. Parks
      {
        id: 'park',
        type: 'fill',
        source: 'mapbox',
        'source-layer': 'landuse',
        filter: ['==', 'class', 'park'],
        paint: {
          'fill-color': colors.park,
          'fill-opacity': 0.7
        }
      },

      // 5. Forest/Wood
      {
        id: 'forest',
        type: 'fill',
        source: 'mapbox',
        'source-layer': 'landuse',
        filter: ['in', 'class', 'wood'],
        paint: {
          'fill-color': colors.forest,
          'fill-opacity': 0.6
        }
      },

      // 6. Buildings - zobrazuje se už od zoomu 6 (aby byly viditelné už od zoomu 13)
      {
        id: 'building',
        type: 'fill',
        source: 'mapbox',
        'source-layer': 'building',
        minzoom: 6, // Nastavit minimální zoom pro zobrazení budov - nižší hodnota = viditelné déle při vzdalování
        layout: {
          visibility: showBuildings ? 'visible' : 'none'
        },
        paint: {
          'fill-color': colors.building,
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6, 0.2,  // Při zoomu 6 jsou budovy jen lehce viditelné
            10, 0.5, // Při zoomu 10 jsou více viditelné
            13, 0.7, // Při zoomu 13 jsou dobře viditelné
            15, 0.85, // Při zoomu 15 jsou téměř plně viditelné
            18, 0.9  // Při zoomu 18 jsou plně viditelné
          ],
          'fill-outline-color': colors.building_outline,
          'fill-antialias': true
        }
      },

      // 6.1. Building outlines (více detailů budov) - zobrazuje se už od zoomu 6 (aby byly viditelné už od zoomu 13)
      {
        id: 'building-outline',
        type: 'line',
        source: 'mapbox',
        'source-layer': 'building',
        minzoom: 6, // Nastavit minimální zoom pro obrysy budov - ještě nižší než hlavní vrstva
        layout: {
          visibility: (showBuildings && isLayerVisible('building-outline', zoom, detailLevel, showMinorRoads, showPaths, showPedestrian, customZoomThresholds)) ? 'visible' : 'none'
        },
        paint: {
          'line-color': colors.building_outline,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6, 0.15, // Při zoomu 6 jsou obrysy velmi tenké
            10, 0.3,
            13, 0.5, // Při zoomu 13 jsou obrysy viditelné
            15, 0.7,
            18, 1.0
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6, 0.2,  // Při zoomu 6 jsou obrysy jen lehce viditelné
            10, 0.4,
            13, 0.65, // Při zoomu 13 jsou obrysy dobře viditelné
            15, 0.85,
            18, 1
          ]
        }
      },

      // 7. Roads - Motorway (viditelné už od zoomu 4)
      {
        id: 'road-motorway',
        type: 'line',
        source: 'mapbox',
        'source-layer': 'road',
        filter: ['==', 'class', 'motorway'],
        layout: {
          visibility: isLayerVisible('road-motorway', zoom, detailLevel, showMinorRoads, showPaths, showPedestrian, customZoomThresholds) ? 'visible' : 'none',
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': colors.road_motorway,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            4, 1.2,
            8, 2.5,
            12, 6,
            15, 9,
            18, 14
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            4, 0.7,
            8, 0.85,
            12, 1
          ]
        }
      },

      // 8. Roads - Major links (ramps) - viditelné už od zoomu 6
      {
        id: 'road-major-link',
        type: 'line',
        source: 'mapbox',
        'source-layer': 'road',
        filter: ['in', 'class', 'trunk_link', 'primary_link'],
        layout: {
          visibility: isLayerVisible('road-major-link', zoom, detailLevel, showMinorRoads, showPaths, showPedestrian, customZoomThresholds) ? 'visible' : 'none',
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': colors.road_major,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6, 0.6,
            8, 1,
            12, 3,
            15, 5,
            18, 8
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6, 0.5,
            8, 0.7,
            12, 0.9
          ]
        }
      },

      // 8.1. Roads - Major (trunk, primary) - viditelné už od zoomu 5
      {
        id: 'road-major',
        type: 'line',
        source: 'mapbox',
        'source-layer': 'road',
        filter: ['in', 'class', 'trunk', 'primary'],
        layout: {
          visibility: isLayerVisible('road-major', zoom, detailLevel, showMinorRoads, showPaths, showPedestrian, customZoomThresholds) ? 'visible' : 'none',
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': colors.road_major,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            5, 1,
            8, 2,
            12, 5,
            15, 7,
            18, 12
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            5, 0.6,
            8, 0.75,
            12, 1
          ]
        }
      },

      // 9. Roads - Minor links (secondary, tertiary links) - viditelné už od zoomu 7
      {
        id: 'road-minor-link',
        type: 'line',
        source: 'mapbox',
        'source-layer': 'road',
        filter: ['in', 'class', 'secondary_link', 'tertiary_link'],
        layout: {
          visibility: isLayerVisible('road-minor-link', zoom, detailLevel, showMinorRoads, showPaths, showPedestrian, customZoomThresholds) ? 'visible' : 'none'
        },
        paint: {
          'line-color': colors.road_minor,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7, 0.4,
            8, 0.7,
            12, 2.5,
            15, 4,
            18, 6
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7, 0.4,
            8, 0.6,
            12, 0.85
          ]
        }
      },

      // 9.1. Roads - Minor (secondary, tertiary) - viditelné už od zoomu 6
      {
        id: 'road-minor',
        type: 'line',
        source: 'mapbox',
        'source-layer': 'road',
        filter: ['in', 'class', 'secondary', 'tertiary'],
        layout: {
          visibility: isLayerVisible('road-minor', zoom, detailLevel, showMinorRoads, showPaths, showPedestrian, customZoomThresholds) ? 'visible' : 'none',
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': colors.road_minor,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6, 0.6,
            8, 1.2,
            12, 3.5,
            15, 5.5,
            18, 9
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6, 0.5,
            8, 0.65,
            12, 0.95
          ]
        }
      },

      // 10. Roads - Street level - viditelné už od zoomu 7
      {
        id: 'road-path',
        type: 'line',
        source: 'mapbox',
        'source-layer': 'road',
        filter: ['in', 'class', 'street'],
        layout: {
          visibility: isLayerVisible('road-path', zoom, detailLevel, showMinorRoads, showPaths, showPedestrian, customZoomThresholds) ? 'visible' : 'none',
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': colors.road_path,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7, 0.4,
            8, 0.6,
            10, 1,
            12, 1.5,
            15, 2.2,
            18, 3.5
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7, 0.5,
            8, 0.6,
            10, 0.75,
            12, 0.9
          ]
        }
      },

      // 11. Roads - Residential (více detailů) - viditelné už od zoomu 7
      {
        id: 'road-residential',
        type: 'line',
        source: 'mapbox',
        'source-layer': 'road',
        filter: ['in', 'class', 'residential', 'service'],
        layout: {
          visibility: isLayerVisible('road-residential', zoom, detailLevel, showMinorRoads, showPaths, showPedestrian, customZoomThresholds) ? 'visible' : 'none',
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': colors.road_path,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7, 0.4,
            8, 0.6,
            10, 0.8,
            12, 1.2,
            15, 1.5,
            18, 2.5
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7, 0.5,
            8, 0.6,
            10, 0.7,
            12, 0.85,
            15, 0.95
          ]
        }
      },

      // 11.1. Roads - Pedestrian areas - viditelné už od zoomu 8
      {
        id: 'road-pedestrian',
        type: 'line',
        source: 'mapbox',
        'source-layer': 'road',
        filter: ['==', 'class', 'pedestrian'],
        layout: {
          visibility: isLayerVisible('road-pedestrian', zoom, detailLevel, showMinorRoads, showPaths, showPedestrian, customZoomThresholds) ? 'visible' : 'none'
        },
        paint: {
          'line-color': colors.road_path,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 0.3,
            10, 0.5,
            12, 0.8,
            15, 1,
            18, 1.5
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 0.4,
            10, 0.5,
            12, 0.65,
            15, 0.75
          ],
          'line-dasharray': [2, 1]
        }
      },

      // 12. Roads - Footways and cycleways - viditelné už od zoomu 9
      {
        id: 'road-footway-cycleway',
        type: 'line',
        source: 'mapbox',
        'source-layer': 'road',
        filter: ['in', 'class', 'footway', 'cycleway', 'steps'],
        layout: {
          visibility: isLayerVisible('road-footway-cycleway', zoom, detailLevel, showMinorRoads, showPaths, showPedestrian, customZoomThresholds) ? 'visible' : 'none'
        },
        paint: {
          'line-color': colors.road_path,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0.25,
            10, 0.4,
            12, 0.6,
            15, 0.8,
            18, 1.2
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0.35,
            10, 0.45,
            12, 0.6,
            15, 0.7
          ],
          'line-dasharray': [1.5, 1]
        }
      },

      // 12.1. Roads - Paths and tracks (ještě více detailů) - viditelné už od zoomu 9
      {
        id: 'road-path-track',
        type: 'line',
        source: 'mapbox',
        'source-layer': 'road',
        filter: ['in', 'class', 'path', 'track', 'bridleway'],
        layout: {
          visibility: isLayerVisible('road-path-track', zoom, detailLevel, showMinorRoads, showPaths, showPedestrian, customZoomThresholds) ? 'visible' : 'none'
        },
        paint: {
          'line-color': colors.road_path,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0.3,
            10, 0.5,
            12, 0.7,
            15, 1,
            18, 1.5
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            9, 0.4,
            10, 0.5,
            12, 0.65,
            15, 0.75
          ],
          'line-dasharray': [1, 1]
        }
      },

      // 13. More landuse types (více detailů krajiny) - viditelné už od zoomu 8
      {
        id: 'landuse-residential',
        type: 'fill',
        source: 'mapbox',
        'source-layer': 'landuse',
        filter: ['==', 'class', 'residential'],
        paint: {
          'fill-color': colors.land,
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 0.1,
            10, 0.15,
            12, 0.2,
            15, 0.25
          ]
        }
      },

      {
        id: 'landuse-industrial',
        type: 'fill',
        source: 'mapbox',
        'source-layer': 'landuse',
        filter: ['==', 'class', 'industrial'],
        paint: {
          'fill-color': colors.building,
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 0.15,
            10, 0.2,
            12, 0.25,
            15, 0.35
          ]
        }
      },

      // 14. More landuse types - commercial areas
      {
        id: 'landuse-commercial',
        type: 'fill',
        source: 'mapbox',
        'source-layer': 'landuse',
        filter: ['==', 'class', 'commercial'],
        paint: {
          'fill-color': colors.building,
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 0.1,
            10, 0.15,
            12, 0.2,
            15, 0.3
          ]
        }
      },

      // 15. More landuse types - agricultural areas
      {
        id: 'landuse-agricultural',
        type: 'fill',
        source: 'mapbox',
        'source-layer': 'landuse',
        filter: ['==', 'class', 'agricultural'],
        paint: {
          'fill-color': colors.land,
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6, 0.1,
            8, 0.15,
            10, 0.2,
            12, 0.25
          ]
        }
      },

      // 16. More landuse types - grass areas
      {
        id: 'landuse-grass',
        type: 'fill',
        source: 'mapbox',
        'source-layer': 'landuse',
        filter: ['==', 'class', 'grass'],
        paint: {
          'fill-color': colors.park,
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 0.2,
            10, 0.3,
            12, 0.4,
            15, 0.5
          ]
        }
      },

      // 17. Place Labels - větší detaily už od zoomu 4
      {
        id: 'place-labels',
        type: 'symbol',
        source: 'mapbox',
        'source-layer': 'place_label',
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            4, 8,
            6, 10,
            8, 12,
            10, 14,
            12, 15,
            15, 18
          ],
          visibility: (showLabels && mapLayer !== 'no-labels') ? 'visible' : 'none'
        },
        paint: {
          'text-color': colors.label_text,
          'text-halo-color': colors.label_halo,
          'text-halo-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            4, 1,
            8, 1.5,
            12, 2
          ]
        }
      },

      // 18. Road Labels (použijeme place_label místo road_label) - větší detaily
      {
        id: 'road-labels',
        type: 'symbol',
        source: 'mapbox',
        'source-layer': 'place_label',
        filter: ['==', 'class', 'city'],
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6, 10,
            8, 11,
            10, 12,
            12, 13,
            15, 14
          ],
          visibility: (showLabels && mapLayer !== 'no-labels') ? 'visible' : 'none'
        },
        paint: {
          'text-color': colors.label_text,
          'text-halo-color': colors.label_halo,
          'text-halo-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            6, 1,
            10, 1.3,
            12, 1.5
          ]
        }
      }
    ]
  };
}

/**
 * Cache pro načtené presety z Supabase (aby se nemusely načítat opakovaně)
 */
let colorPresetsCache: Map<string, LayerColors> = new Map();

/**
 * Načte defaultní barvy z Supabase podle colorScheme
 * Vrací null pokud se nepodařilo načíst - barvy MUSÍ být v Supabase!
 */
async function getDefaultColorsFromSupabase(colorScheme: string): Promise<LayerColors | null> {
  // Zkontrolovat cache
  if (colorPresetsCache.has(colorScheme)) {
    return colorPresetsCache.get(colorScheme)!;
  }

  try {
    const result = await getColorPresetByScheme(colorScheme);

    if (result.success && result.data) {
      const colors = result.data.colors;
      // Uložit do cache
      colorPresetsCache.set(colorScheme, colors);
      return colors;
    }

    console.error(`Failed to load color preset from Supabase for ${colorScheme}:`, result.error);
  } catch (error) {
    console.error(`Error loading color preset from Supabase for ${colorScheme}:`, error);
  }

  return null;
}

/**
 * Pomocná funkce pro získání barev z MapSettings - pouze z Supabase!
 * 1. colors (z color presetu)
 * 2. customColors (starý způsob, pro kompatibilitu - deprecated)
 * 3. Načtení z Supabase podle colorScheme (asynchronní - použijte getColorsFromSettingsAsync)
 * 
 * POZNÁMKA: Tato synchronní verze vrátí prázdný objekt pokud barvy nejsou nastavené.
 * Pro načítání z Supabase použijte getColorsFromSettingsAsync().
 */
export function getColorsFromSettings(settings: {
  colors?: LayerColors;
  customColors?: LayerColors;
  colorScheme: string;
}): LayerColors {
  if (settings.colors) {
    return settings.colors;
  }
  // @deprecated - customColors je zastaralé, používejte colors
  if (settings.customColors) {
    return settings.customColors;
  }
  // Pro synchronní použití nelze načítat z Supabase
  // Vraťme prázdný objekt - barvy MUSÍ být nastavené před voláním této funkce
  // console.warn(`No colors set in settings. Colors must be loaded from Supabase first using getColorsFromSettingsAsync().`);
  return {
    background: '#FFFFFF',
    water: '#CCCCCC',
    land: '#FFFFFF',
    park: '#CCCCCC',
    forest: '#CCCCCC',
    building: '#CCCCCC',
    building_outline: '#999999',
    road_motorway: '#666666',
    road_major: '#999999',
    road_minor: '#CCCCCC',
    road_path: '#E0E0E0',
    label_text: '#000000',
    label_halo: '#FFFFFF',
  };
}

/**
 * Asynchronní verze - načte barvy z Supabase
 * Jediný způsob jak získat barvy pokud nejsou explicitně nastavené
 */
export async function getColorsFromSettingsAsync(settings: {
  colors?: LayerColors;
  customColors?: LayerColors;
  colorScheme: string;
}): Promise<LayerColors> {
  if (settings.colors) {
    return settings.colors;
  }
  // @deprecated - customColors je zastaralé
  if (settings.customColors) {
    return settings.customColors;
  }
  // Načíst z Supabase
  const colors = await getDefaultColorsFromSupabase(settings.colorScheme);
  if (colors) {
    return colors;
  }

  // Pokud se nepodařilo načíst, vyhodit chybu - barvy MUSÍ být v Supabase!
  throw new Error(`Color preset for colorScheme "${settings.colorScheme}" not found in Supabase. Please ensure presets are seeded in the database.`);
}

